import React from "react"
import clsx from "clsx"
import { isMobileOnly } from "react-device-detect"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { privateRoutes } from "router/routes"
import * as BikeApi from "features/bike/api/BikeApi"

import { useAlert, useAuth, useModal, useBikeStoreProcess } from "hooks"

import FormElement from "components/Form/FormElement"
import { normalizeBoolean } from "utils/functions"
import { getEntityId, getOldStoreId } from "utils/user"
import {
	userEntityGenderList,
	BIKE_BACK_STORE,
	INTER_CHAIN,
	MFC_OPERATOR,
	MFC_RETAIL_GENDER,
	VELOCISTE_GENDER,
	ARCADE_STOREID,
	ARCADE_STOREID_ST,
	OWNER_PRIVATE_GENDER,
	OWNER_COMPANY_GENDER,
} from "utils/constants"
import { BikeDescription, BikeOwner, BikeToRegister, ErrorResponse } from "models"
import { FormError } from "models/form"

const defaultFormEvents = {
	ready: true,
}

export const FormSellBike: React.FC = () => {
	const { bikeProcessState, setInitialBike } = useBikeStoreProcess()
	const { currentBike: bike } = bikeProcessState

	const [formFields, setFormFields] = React.useState(bike)
	const [formEvents, setFormEvents] = React.useState(defaultFormEvents)

	const [errors, setErrors] = React.useState([])
	const [showLocalErrors, setShowLocalErrors] = React.useState(false)
	const [apiError, setApiError] = React.useState(null)

	const { alert } = useAlert()
	const { showModal } = useModal()
	const { t } = useTranslation()
	const { entity } = useAuth()
	const navigate = useNavigate()

	const isManufacturerNFT = bike.description_made_by === "MANUFACTURER_NFT"
	const isVelociste = entity.gender === VELOCISTE_GENDER
	const LONG_EMAIL_LENGTH = 30 // On considère qu'un mail est long à partir de 30 caractères, c'est toujours possible de changer cette valeur

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const target = event.target
		const value = target.type === "checkbox" ? target.checked : target.value
		const name = target.name
		setFormFields({
			...formFields,
			[name]: value,
		})
	}

	const handleValidation = React.useCallback((name: string, errs: Array<FormError>) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
	}, [])

	const handleSubmit = async (event: React.SyntheticEvent) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)
		const isLongEmail = formFields?.mail?.length > LONG_EMAIL_LENGTH

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			setApiError(null)
			// Open confirmation modal
			showModal(ModalConfirmation, {
				body: () => (
					<>
						<h2 className="c-h2 text-primary font-bold text-center mt-7 mb-12">
							{t("sell_modal_confirm_title", { ns: "bike" })}
						</h2>
						<p className="mb-2">
							{t("sell_modal_confirm_description", { ns: "bike" })}
						</p>
						<div className="l-grid u-mg-negative-hz-m mb-12">
							<div className="lg:w-1/2 w-full p-5">
								<p className="">
									{t("sell_modal_confirm_bikeCode", { ns: "bike" })}
								</p>
								<p className="text-primary font-bold lg:text-m">{bike?.code}</p>
							</div>
							<div className="lg:w-1/2 w-full p-5">
								<p className="">
									{t("sell_modal_confirm_ownerMail", { ns: "bike" })}
								</p>
								<p
									className={clsx("text-primary font-bold lg:text-m", {
										"break-words": isLongEmail,
									})}
								>
									{formFields?.mail}
								</p>
							</div>
						</div>
					</>
				),
				callback: async () => {
					// hide error
					setShowLocalErrors(false)
					// prevent form to be submitted again
					setFormEvents({
						...formEvents,
						ready: false,
					})

					// to be able to register it, you must ensure that the store is authorized by the operator
					const isAble = entity?.entity_data?.entity_permission?.operateurs.find(
						(oneOperator) => oneOperator === bike?.operator
					)
					if (typeof isAble === "undefined") {
						alert("error", t("sell_error_NotAuthorizedException", { ns: "api" }))
						setFormEvents({
							...formEvents,
							ready: true,
						})
						return
					}

					// if status 6 (retour magasin), on contrôle que ce magasin est autorisé
					if (bike.status === BIKE_BACK_STORE) {
						const bikeStoreId = bike.storeId
						const storeStoreId = "ST-" + getEntityId()
						const oldStoreId = getOldStoreId()

						// Un vendeur MFC avec un gender Intersport doit pouvoir revendre un vélo MFC status 6 d'un autre vélociste
						// Attention, ici on fait aussi un comparatif avec le store id Arcade
						const isMFCIntersportSeller =
							entity?.entity_data?.code_chaine === INTER_CHAIN
						const isMFCBike = bike?.operator === MFC_OPERATOR

						if (
							bikeStoreId !== storeStoreId &&
							bikeStoreId !== oldStoreId &&
							bikeStoreId !== ARCADE_STOREID &&
							bikeStoreId !== ARCADE_STOREID_ST &&
							!(isMFCIntersportSeller && isMFCBike)
						) {
							alert("error", t("sell_error_NotAuthorizedException", { ns: "api" }))
							setFormEvents({
								...formEvents,
								ready: true,
							})
							return
						}
					}

					// 1. Create bike_description
					// --------
					const bike_description: BikeDescription = {
						gear_type_id: bike.gear_type_id, // @TODO
						gear_type: bike.gear_type,
						brand_id: bike.brand_id,
						brand: bike.brand,
						model: bike.model,
						color: bike.color,
						is_electric: normalizeBoolean(bike.isVAE?.toString()),
					}
					if (bike.frame_serial_number) {
						bike_description.frame_serial_number = bike.frame_serial_number
					}
					if (normalizeBoolean(bike.isVAE?.toString()) && bike.engine_serial_number) {
						bike_description.engine_serial_number = bike.engine_serial_number
					}
					if (normalizeBoolean(bike.isVAE?.toString()) && bike.battery_serial_number) {
						bike_description.battery_serial_number = bike.battery_serial_number
					}

					// 2. Create bike_owner
					// --------
					const bike_owner: BikeOwner = {
						...(formFields.isWallet
							? { consumer_id: formFields.consumerId } // si isWallet, on envoie à l'API seulement le consumerId
							: {
									gender: parseInt(formFields.gender?.toString()),
									phone: formFields.phone,
									mail: formFields.mail.trim(),
									...(parseInt(formFields.gender?.toString()) ===
									OWNER_PRIVATE_GENDER
										? {
												// Si particulier
												first_name: formFields.first_name,
												last_name: formFields.last_name,
											}
										: {
												// Si pro
												social_reason: formFields.social_reason,
											}),
								}),
					}

					// 3. Create the new bike to be sent
					// ------------
					let template = "default"
					if (bike?.operator === MFC_OPERATOR) {
						const operator_detail = entity?.data_operators.find(
							(oneOperator) => oneOperator.operator === bike?.operator
						)
						const operator_gender = operator_detail?.entity_data?.operator_gender
						if (operator_gender !== MFC_RETAIL_GENDER) {
							template = operator_detail?.entity_data?.operator_gender
						}
					}

					const newBike: BikeToRegister = {
						bike_id: bike.code,
						bike_status: 1,
						bike_description: bike_description,
						bike_owner: bike_owner,
						send_notification_by_mail: template, //sunn
						send_receipt_by_mail: template, //sunn
						send_pro_sale_event: isManufacturerNFT,
					}

					try {
						const { res, getError } = await BikeApi.createBike(newBike)
						if (getError()) throw getError()

						// We need bike operator for set a logo in the PDF
						newBike.operator = bike.operator
						alert(
							"success",
							t("sell_success", { ns: "bike", bikeCode: formFields?.bikeCode })
						)

						console.log("res", res)

						// Updating bike info. Here, we need all information about bike owner for the PDF
						// If we fetch bike, firstname, lastname and phone are no longer accessible
						// Then, redirect to BikeUpdated

						// Dans le cas d'un "isWallet", on n'envoie que le consumer_id à l'API
						// Dans l'écran suivant "BikeUpdatedPage", nous ne retrouvons donc pas les infos de l'acheteur
						// On reconstruit donc ici les infos de l'acheteur que l'on passe à notre context via setInitialBike
						const bike_owner = {
							gender: parseInt(formFields.gender?.toString()),
							phone: formFields.phone,
							mail: formFields.mail.trim(),
							...(parseInt(formFields.gender?.toString()) === OWNER_PRIVATE_GENDER
								? {
										first_name: formFields.first_name,
										last_name: formFields.last_name,
									}
								: {}),
							...(parseInt(formFields.gender?.toString()) === OWNER_COMPANY_GENDER
								? {
										social_reason: formFields.social_reason,
									}
								: {}),
						}
						setInitialBike({
							...newBike,
							...{ bike_owner: bike_owner },
							ocode_id: "",
							create_date: "",
							modify_date: "",
							row_version: "",
							consumer_id: "",
							bike_owner_store_id: "",
							mail_verified: false,
							account_type: "",
							operator: bike?.operator, // Add the 'operator' property
						})
						navigate(
							{
								pathname: `${privateRoutes.updatedBike.basePath}/${bike?.code}`,
							},
							{ state: { variant: "sell-succcess" } }
						)
					} catch (error) {
						console.error(error)
						const erroMsg = (error as ErrorResponse)?.message?.toString()
						if (erroMsg) alert("error", erroMsg)
					} finally {
						setFormEvents((s) => ({
							...s,
							ready: true,
						}))
					}
				},
			})
		}
	}

	return (
		<form data-cy="form-sell-bike">
			<section className="mb-5">
				<h2 className="c-h2 font-semibold mb-5">
					{t("sell_update_owner_title", { ns: "bike" })}
				</h2>
				<div className="l-grid flex-col u-mg-negative-hz-m">
					<div className="lg:w-1/2 w-full p-5">
						<h3 className="c-h4 uppercase mb-7">
							{t("sell_update_owner_title_manually", { ns: "bike" })}
						</h3>
						{/* VAE */}
						<FormElement
							value={formFields.gender}
							name="gender"
							type="radio"
							options={userEntityGenderList}
							className="mb-7"
							classNameOptionsContainer={clsx("flex-row w-full", {
								"flex-col": isMobileOnly,
							})}
							classNameLabel="mb-5"
							classNameOption="flex-1 items-center"
							required={true}
							showErrors={showLocalErrors}
							onValidate={handleValidation}
							onChange={handleInputChange}
						/>
						{parseInt(formFields.gender?.toString()) === 1 && ( // private
							<>
								{/* last name */}
								<FormElement
									value={formFields.last_name}
									name="last_name"
									type="text"
									label={t("owner.name", { ns: "bike" })}
									className="mb-7"
									required={true}
									showErrors={showLocalErrors}
									onValidate={handleValidation}
									onChange={handleInputChange}
								/>
								{/* first name */}
								<FormElement
									value={formFields.first_name}
									name="first_name"
									type="text"
									label={t("owner.firstname", { ns: "bike" })}
									className="mb-7"
									required={true}
									showErrors={showLocalErrors}
									onValidate={handleValidation}
									onChange={handleInputChange}
								/>
							</>
						)}
						{parseInt(formFields.gender?.toString()) === 2 && ( // company
							<>
								{/* social_reason */}
								<FormElement
									value={formFields.social_reason}
									name="social_reason"
									type="text"
									label={t("owner.raison_sociale", { ns: "bike" })}
									className="mb-7"
									required={true}
									showErrors={showLocalErrors}
									onValidate={handleValidation}
									onChange={handleInputChange}
								/>
							</>
						)}

						{/* phone */}
						<FormElement
							value={formFields.phone}
							name="phone"
							type="phone"
							label={t("owner.phonenumber", { ns: "bike" })}
							className="mb-7"
							required={true}
							showErrors={showLocalErrors}
							onValidate={handleValidation}
							onChange={handleInputChange}
						/>

						{/* mail */}
						<FormElement
							value={formFields.mail}
							name="mail"
							type="email"
							label={t("owner.mail", { ns: "bike" })}
							className="mb-7"
							required={true}
							showErrors={showLocalErrors}
							onValidate={handleValidation}
							onChange={handleInputChange}
						/>

						{/* ownerConfirmNewMail */}
						{/* Not required for asso */}
						{isVelociste && (
							<FormElement
								value={formFields.ownerConfirmNewMail}
								name="ownerConfirmNewMail"
								type="email"
								label={t("owner.newMailConfirmation", { ns: "bike" })}
								mailToConfirm={formFields.mail}
								className="mb-5"
								required={true}
								showErrors={showLocalErrors}
								onValidate={handleValidation}
								onChange={handleInputChange}
							/>
						)}
					</div>
				</div>
			</section>
			{apiError && (
				<div className="mb-7">
					<p className="text-danger">{apiError}</p>
				</div>
			)}
			<div className="flex justify-end mt-7 mb-5">
				{formEvents.ready && (
					<button
						type="submit"
						className="c-btn c-btn--primary px-12"
						onClick={handleSubmit}
						// disabled={errors.length > 0}
					>
						{t("sell", { ns: "bike" })}
					</button>
				)}
				{!formEvents.ready && <div className="c-spinner"></div>}
			</div>
		</form>
	)
}

const ModalConfirmation = ({ body, callback }) => {
	const { hideModal } = useModal()
	const { t } = useTranslation()
	const validate = () => {
		hideModal()
		callback()
	}
	return (
		<div className="px-16 py-10">
			{React.createElement(body)}
			<div className="flex justify-between flex-col md:flex-row-reverse">
				<button
					data-cy="sell-bike-validate"
					type="button"
					className="c-btn c-btn--primary px-12 mb-5 lg:m-0"
					onClick={validate}
				>
					{t("global.validate")}
				</button>
				<button
					data-cy="sell-bike-cancel"
					type="button"
					className="c-btn c-btn--white-bordered px-7 mb-5 lg:m-0"
					onClick={hideModal}
				>
					{t("global.cancel")}
				</button>
			</div>
		</div>
	)
}
